import React, { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ClipboardList, Plus, Trash2, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import { QuickNotesSettings } from "@/components/booking/QuickNotesSettings";

const uid = () => Math.random().toString(36).slice(2, 10);
const FIELD_TYPES = [
  ["heading", "Heading"], ["text", "Text note"], ["short", "Short answer"], ["long", "Long answer"],
  ["choose", "Choose one"], ["multichoice", "Choose many"], ["number", "Number"], ["checkbox", "Checkbox"], ["date", "Date"],
];
const TYPE_LABEL = { laser: "Laser / Hair", dermal: "Dermal", injectable: "Injectable", generic: "Notes" };
const blank = () => ({ name: "", chart_type: "generic", description: "", service_ids: [], has_body_map: true, has_photos: true, fields: [] });

export default function ChartTemplates() {
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/chart-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { load(); api.get("/services").then(({ data }) => setServices(data)).catch(() => {}); }, []);

  const openEdit = (t) => setEditing({ ...t, fields: (t.fields || []).map((f) => ({ ...f })) });
  const addField = (type) => setEditing((s) => ({ ...s, fields: [...s.fields, { id: uid(), type, label: "", required: false, options: [] }] }));
  const updField = (i, k, v) => setEditing((s) => ({ ...s, fields: s.fields.map((f, j) => (j === i ? { ...f, [k]: v } : f)) }));
  const rmField = (i) => setEditing((s) => ({ ...s, fields: s.fields.filter((_, j) => j !== i) }));
  const move = (i, d) => setEditing((s) => { const a = [...s.fields]; const j = i + d; if (j < 0 || j >= a.length) return s; [a[i], a[j]] = [a[j], a[i]]; return { ...s, fields: a }; });
  const toggleService = (sid) => setEditing((s) => ({ ...s, service_ids: s.service_ids.includes(sid) ? s.service_ids.filter((x) => x !== sid) : [...s.service_ids, sid] }));

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Give the chart a name");
    const payload = { name: editing.name, chart_type: editing.chart_type, description: editing.description || "", service_ids: editing.service_ids || [], has_body_map: editing.has_body_map, has_photos: editing.has_photos, fields: editing.fields };
    try {
      if (editing.id) await api.put(`/chart-templates/${editing.id}`, payload);
      else await api.post("/chart-templates", payload);
      toast.success("Chart template saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  const remove = async (t) => { if (!window.confirm(`Delete the "${t.name}" chart template?`)) return; await api.delete(`/chart-templates/${t.id}`); toast.success("Deleted"); load(); };

  const [tab, setTab] = useState("charts");
  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="chart-templates-page">
      <div className="inline-flex rounded-lg border border-border p-0.5 mb-5 text-sm" data-testid="charting-tabs">
        {[["charts", "Chart templates"], ["quick", "Quick notes"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3.5 h-8 rounded-md transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid={`charting-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "quick" && <QuickNotesSettings />}
      <div className={tab === "quick" ? "hidden" : ""}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2"><ClipboardList className="h-7 w-7 text-primary" />Treatment charts</h1>
          <p className="text-muted-foreground mt-1">Customisable clinical charts, linked to services and used at each appointment.</p>
        </div>
        <Button className="rounded-lg" onClick={() => setEditing(blank())} data-testid="new-chart-template-button"><Plus className="h-4 w-4 mr-1.5" />New chart</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <Card key={t.id} className="rounded-2xl border p-5 shadow-sm flex flex-col" data-testid={`chart-template-card-${t.id}`}>
            <div className="flex items-center gap-2">
              <div className="font-heading font-semibold flex-1">{t.name}</div>
              <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wide">{TYPE_LABEL[t.chart_type]}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1 flex-1">{t.description || `${(t.fields || []).length} fields`}{t.service_ids?.length ? ` · ${t.service_ids.length} service(s)` : ""}</p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" className="rounded-lg flex-1" onClick={() => openEdit(t)} data-testid={`chart-template-edit-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
              <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={() => remove(t)} data-testid={`chart-template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
        {templates.length === 0 && <p className="text-muted-foreground">No chart templates yet.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="chart-template-editor">
          <DialogHeader><DialogTitle className="font-heading">{editing?.id ? "Edit chart template" : "New chart template"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Chart name" value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} className="h-11 rounded-lg" data-testid="chart-template-name" />
                <Select value={editing.chart_type} onValueChange={(v) => setEditing((s) => ({ ...s, chart_type: v }))}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="chart-template-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input placeholder="Short description (optional)" value={editing.description} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} className="h-10 rounded-lg" data-testid="chart-template-desc" />

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.has_body_map} onCheckedChange={(v) => setEditing((s) => ({ ...s, has_body_map: v }))} data-testid="chart-template-bodymap" />Body map</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.has_photos} onCheckedChange={(v) => setEditing((s) => ({ ...s, has_photos: v }))} data-testid="chart-template-photos" />Photos</label>
              </div>

              {services.length > 0 && (
                <div>
                  <div className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Use for services (optional)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {services.map((sv) => (
                      <button type="button" key={sv.id} onClick={() => toggleService(sv.id)}
                        className={`px-2.5 py-1 rounded-full text-xs border ${editing.service_ids.includes(sv.id) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                        data-testid={`chart-template-service-${sv.id}`}>{sv.name}</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {editing.fields.map((f, i) => (
                  <div key={f.id} className="rounded-xl border border-border p-3 space-y-2" data-testid={`chart-field-row-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground">{FIELD_TYPES.find((x) => x[0] === f.type)?.[1] || f.type}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground"><ArrowUp className="h-4 w-4" /></button>
                        <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground"><ArrowDown className="h-4 w-4" /></button>
                        <button onClick={() => rmField(i)} className="text-muted-foreground hover:text-destructive" data-testid={`chart-field-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <Input placeholder={f.type === "text" ? "Note text" : "Field label"} value={f.label} onChange={(e) => updField(i, "label", e.target.value)} className="h-9 rounded-lg" data-testid={`chart-field-label-${i}`} />
                    {(f.type === "choose" || f.type === "multichoice") && (
                      <Input placeholder="Options, comma separated" value={(f.options || []).join(", ")} onChange={(e) => updField(i, "options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="h-9 rounded-lg" data-testid={`chart-field-options-${i}`} />
                    )}
                    {!["heading", "text"].includes(f.type) && (
                      <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.required} onCheckedChange={(v) => updField(i, "required", v)} data-testid={`chart-field-required-${i}`} />Required</label>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <div className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Add field</div>
                <div className="flex flex-wrap gap-2">
                  {FIELD_TYPES.map(([type, label]) => (
                    <Button key={type} variant="outline" size="sm" className="rounded-lg" onClick={() => addField(type)} data-testid={`add-chart-field-${type}`}><Plus className="h-3.5 w-3.5 mr-1" />{label}</Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} data-testid="chart-template-save">Save chart</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
